import React, { Fragment, useEffect, useState, useMemo } from "react";
import { CardBody, Col, Row, Table, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Card, CardHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import moment from "moment";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/logos/logo_preta.jpeg';
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const STATUS_MAP = {
  4: 'Livre',
  2: 'Reservado',
  3: 'Vendido', 
  1: 'Cancelado',
  5: 'Renovado',
  6: 'Pendente Renovação'
};

const optionsStatus = [
  { value: '1', label: 'Livre' },
  { value: '2', label: 'Reservado' },
  { value: '3', label: 'Vendido' },
  { value: '5', label: 'Renovado' },
  { value: '6', label: 'Pend. Renovação' },
  { value: 'cancelado', label: 'Cancelado' }
];

const formatarData = (dataString) => {
  if (!dataString) return '--';
  try {
    return moment(dataString).format('DD/MM/YYYY HH:mm');
  } catch (e) {
    return '--';
  }
};

const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return 'R$ --';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
};

const getNestedValue = (obj, path, defaultValue = null) => {
    if (!path) return defaultValue;
    const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
    return value !== undefined && value !== null ? value : defaultValue;
};

const escapeXml = (unsafe) => {
    if (unsafe === null || unsafe === undefined) return '';
    return unsafe.toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
};

const TabelaFiltroPaginacao = ({
  columns, data, isGlobalFilter, customPageSize, tableClassName, theadClassName,
  trClassName, thClassName, divClassName, SearchPlaceholder, filters = [],
  nomeRelatorio, hiddenColumns = [], feirasOptions = [], feiraSelecionada, onFeiraChange,
  paisesOptions = [], paisSelecionado, onPaisChange, estadosOptions = [], estadoSelecionado,
  onEstadoChange, cidadesOptions = [], cidadeSelecionada, onCidadeChange,
  pavilhoesOptions = [], pavilhaoSelecionado, onPavilhaoChange, dashboard, feiraRenovada, 
  statusSelecionado, onStatusChange,
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isExportacaoAvancadaOpen, setIsExportacaoAvancadaOpen] = useState(false);
  const [camposSelecionados, setCamposSelecionados] = useState([]);
  const [ordenacao, setOrdenacao] = useState('updated_at');
  const [ordemDescendente, setOrdemDescendente] = useState(true);
  const [agrupamento, setAgrupamento] = useState('');

  const getStatus = (statusId) => {
    switch(statusId) {
      case 1: return 'Livre';
      case 2: return 'Reservado';
      case 3: return 'Vendido';
      case 5: return 'Renovado';
      case 6: return 'Pend.Renovação';
      default: return 'Cancelado';
    }
  };
  
  const camposDasColunas = useMemo(() => {
    return columns
      .filter(col => col.header && col.accessorKey && !col.header.includes('Ação'))
      .map(col => {
        let formatter = (value) => value ?? '--';
        let type = 'string';

        if (['reserva_valida_ate', 'created_at', 'updated_at'].includes(col.accessorKey)) {
          formatter = formatarData;
          type = 'datetime';
        } else if (col.accessorKey.toLowerCase().includes('valor')) {
          formatter = formatarMoeda;
          type = 'currency';
        } else if (col.accessorKey.toLowerCase().includes('area')) {
          formatter = formatNumber;
          type = 'number';
        } else if (col.accessorKey === 'status_reserva_id') { 
            formatter = (item) => {
                if (!item) return '--'; // Proteção para dados nulos

                const statusId = item;
                switch(statusId) {
                    case 1: return 'Livre';
                    case 2: return 'Reservado';
                    case 3: return 'Vendido';
                    case 5: return 'Renovado';
                    case 6: return 'Pend.Renovação';
                    default: return 'Desconhecido';
                }
            };
            type = 'string';
          }
        const accessor = (item) => formatter(getNestedValue(item, col.accessorKey));
        return { id: col.accessorKey, label: col.header, accessor, type };
      });
  }, [columns]);

  const camposAdicionais = useMemo(() => [
    { id: 'expositor_nome', label: 'Nome Fantasia Expositor', accessor: item => getNestedValue(item, 'expositor_nome'), type: 'string' },
    { id: 'expositor_endereco', label: 'Endereço Expositor', accessor: item => getNestedValue(item, 'expositor_endereco'), type: 'string' },
    { id: 'expositor_email', label: 'E-mail Expositor', accessor: item => getNestedValue(item, 'expositor_email'), type: 'string' },
    { id: 'expositor_telefone', label: 'Telefone Expositor', accessor: item => getNestedValue(item, 'expositor_telefone'), type: 'string' },
    { id: 'expositor_whatsapp', label: 'Whatsapp Expositor', accessor: item => getNestedValue(item, 'expositor_whatsapp'), type: 'string' },
    { id: 'valor_total_estande_metro', label: 'Valor Base Espaço', accessor: item => formatarMoeda(getNestedValue(item, 'valor_total_estande_metro', 0)), type: 'currency' },
   // (m²)', accessor: item => formatNumber(getNestedValue(item, 'area', 0)), type: 'number' },
    { id: 'metragem_bonificada', label: 'M² Bonificada', accessor: item => formatNumber(getNestedValue(item, 'metragem_bonificada', 0)), type: 'number' },
    { id: 'valor_kva_tabela', label: 'Valor KVA', accessor: item => formatarMoeda(getNestedValue(item, 'valor_kva_tabela', 0)), type: 'currency' },
    { id: 'quantidade_kva_tabela', label: 'Quantidade KVA', accessor: item => formatNumber(getNestedValue(item, 'quantidade_kva_tabela', 0)), type: 'number' },
    { id: 'valor_estacionamento', label: 'Valor Estacionamento', accessor: item => formatarMoeda(getNestedValue(item, 'valor_estacionamento', 0)), type: 'currency' },
    { id: 'quantidade_estacionamento', label: 'Quantidade Estacionamento', accessor: item => formatNumber(getNestedValue(item, 'quantidade_estacionamento', 0)), type: 'number' },
    { id: 'valor_total_adicionais', label: 'Valor Adicionais', accessor: item => formatarMoeda(getNestedValue(item, 'valor_total_adicionais', 0)), type: 'currency' },
    { id: 'valor_final_calculado', label: 'Valor Final Calculado', accessor: item => formatarMoeda(getNestedValue(item, 'valor_final_calculado', 0)), type: 'currency' },
  ], []);

  const camposDisponiveis = useMemo(() => [...camposDasColunas, ...camposAdicionais], [camposDasColunas, camposAdicionais]);

  const columnVisibility = useMemo(() => hiddenColumns.reduce((acc, colId) => ({ ...acc, [colId]: false }), {}), [hiddenColumns]);

  useEffect(() => {
    if (camposDasColunas.length > 0 && camposSelecionados.length === 0) {
      setCamposSelecionados(camposDasColunas.map(c => c.id));
    }
  }, [camposDasColunas]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const stringValue = String(getNestedValue(row.original, columnId, ''));
    const itemRank = rankItem(stringValue, value);
    addMeta({ itemRank });
    return itemRank.passed;
  };
  
   const exactFilter = (row, columnId, value) => {
    if (!value) return true;
    const cellValue = getNestedValue(row.original, columnId, '');
    return String(cellValue) === value;
  };

  const filterFns = useMemo(() => ({
    fuzzy: fuzzyFilter,
    exact: exactFilter
  }), []);

  const table = useReactTable({
    columns, data, filterFns,
    initialState: { columnVisibility },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters, onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter, getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
  const map = {
    expositor_pais:    paisSelecionado || undefined,
    expositor_estado:  estadoSelecionado || undefined,
    expositor_cidade:  cidadeSelecionada || undefined,
    pavilhao_nome:     pavilhaoSelecionado || undefined,
  };

  Object.entries(map).forEach(([columnId, value]) => {
    const col = table.getColumn(columnId);
    if (col) col.setFilterValue(value);
  });
}, [table, paisSelecionado, estadoSelecionado, cidadeSelecionada, pavilhaoSelecionado]);

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage, getPageOptions, setPageIndex, nextPage, previousPage, getState } = table;

  const toggleCampo = (campoId) => {
    setCamposSelecionados(prev => prev.includes(campoId) ? prev.filter(id => id !== campoId) : [...prev, campoId]);
  };

  const ordenarDados = (dados) => {
    return [...dados].sort((a, b) => {
      let valorA = getNestedValue(a, ordenacao, '');
      let valorB = getNestedValue(b, ordenacao, '');
      if (typeof valorA === 'string' && valorA.startsWith('R$')) {
        valorA = parseFloat(valorA.replace(/[R$.]/g, '').replace(',', '.').trim());
        valorB = parseFloat(String(valorB).replace(/[R$.]/g, '').replace(',', '.').trim());
      }
      if (typeof valorA === 'string') return ordemDescendente ? valorB.localeCompare(valorA) : valorA.localeCompare(valorB);
      return ordemDescendente ? valorB - valorA : valorA - valorB;
    });
  };

  const contarEspacosPorStatus = (dados) => {
    return {
      vendidos: dados.filter(item => item.status_reserva_id === 3).length,
      renovados: dados.filter(item => item.status_reserva_id === 5).length, // Novo
      pendentes: dados.filter(item => item.status_reserva_id === 6).length, // Novo
      livres: dados.filter(item => item.status_reserva_id === 1).length,
      reservados: dados.filter(item => item.status_reserva_id === 2).length,
      cancelados: dados.filter(item => item.cancelado === true).length
    };
  };

  const calcularTodosResumos = (dadosOrdenados, camposSelecionados) => {
    let camposParaExportar = camposDisponiveis.filter(c => camposSelecionados.includes(c.id));

    const criarColunaAgrupada = (camposOriginais, idsParaAgrupar, novaColunaId, novaColunaLabel) => {
        const camposSelecionadosParaAgrupar = camposOriginais.filter(c => idsParaAgrupar.includes(c.id));
        if (camposSelecionadosParaAgrupar.length > 0) {
            const colunaAgrupada = {
                id: novaColunaId,
                label: novaColunaLabel,
                accessor: (item) => camposSelecionadosParaAgrupar
                    .map(campo => `${campo.label}: ${campo.accessor(item)}`)
                    .filter(v => !v.endsWith(': --') && !v.endsWith(': '))
                    .join('\n')
            };
            const camposRestantes = camposOriginais.filter(c => !idsParaAgrupar.includes(c.id));
            return [colunaAgrupada, ...camposRestantes];
        }
        return camposOriginais;
    };

    const idsExpositor = ['expositor_nome', 'expositor_endereco', 'expositor_email', 'expositor_telefone', 'expositor_whatsapp'];
    const camposAposAgruparExpositor = criarColunaAgrupada(camposParaExportar, idsExpositor, 'detalhesExpositor', 'Detalhes do Expositor');
    
    let camposProcessados = [...camposAposAgruparExpositor];
    const idxDetalhesExpositor = camposProcessados.findIndex(c => c.id === 'detalhesExpositor');
    if (idxDetalhesExpositor > -1) {
        const [detalhesExpositor] = camposProcessados.splice(idxDetalhesExpositor, 1);
        const idxFeira = camposProcessados.findIndex(c => c.id === 'feira.nome');
        camposProcessados.splice(idxFeira !== -1 ? idxFeira + 1 : 1, 0, detalhesExpositor);
    }
    
    const idsEstande = ['valor_total_estande_metro', 'area', 'metragem_bonificada', 'valor_kva_tabela', 'valor_estacionamento', 'valor_total_adicionais', 'valor_final_calculado', 'quantidade_estacionamento', 'quantidade_kva_tabela'];
    camposProcessados = criarColunaAgrupada(camposProcessados, idsEstande, 'detalhesEstande', 'Detalhes do Espaço');

    const idxDetalhesEstande = camposProcessados.findIndex(c => c.id === 'detalhesEstande');
    if (idxDetalhesEstande > -1) {
        const [detalhesEstande] = camposProcessados.splice(idxDetalhesEstande, 1);
        const idxNomeEstande = camposProcessados.findIndex(c => c.id === 'nome_estande');
        camposProcessados.splice(idxNomeEstande !== -1 ? idxNomeEstande + 1 : camposProcessados.length, 0, detalhesEstande);
    }
    
    camposParaExportar = camposProcessados;
    
    // const totalEspacosVendidos = dadosOrdenados.length;
    const contagemStatus = contarEspacosPorStatus(dadosOrdenados);
    const totalEspacosVendidos = contagemStatus.vendidos; // Vendidos
    const totalEspacosRenovados = contagemStatus.renovados; // Vendidos + Renovados
    const totalEspacosPendentes = contagemStatus.pendentes; // Vendidos + Renovados
    const totalEspacosReservados = contagemStatus.reservados; // Vendidos + Renovados
    const totalEspacosLivres = contagemStatus.livres; // Vendidos + Renovados

    const metragemRenovada = dadosOrdenados.filter(item => item.status_reserva_id === 5).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Renovada
    const metragemPendenteRenovacao = dadosOrdenados.filter(item => item.status_reserva_id === 6).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Pen.Renovacao

    const metragemVendida = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0);
    const metragemBonificada = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'metragem_bonificada', 0) || 0), 0);
    const metragemLivre = dadosOrdenados.filter(item => item.status_reserva_id === 1).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Livre
    const metragemReservada = dadosOrdenados.filter(item => item.status_reserva_id === 2).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Reservada
    const valorTotalVendido = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_final_calculado', 0) || 0), 0);
    const metragemCobrada = metragemVendida - metragemBonificada;
    const valorEspaco = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_total_estande_metro', 0) || 0), 0);
    const valorKVA = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_kva_tabela', 0) || 0), 0);
    const valorEstacionamento = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_estacionamento', 0) || 0), 0);
    const quantidadeKVA = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'quantidade_kva_tabela', 0) || 0), 0);
    const quantidadeEstacionamento = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'quantidade_estacionamento', 0) || 0), 0);
    
    const totaisAdicionais = dadosOrdenados.reduce((acc, item) => {
        const adicionais = getNestedValue(item, 'adicionais_detalhados', []);
        if (Array.isArray(adicionais)) {
            adicionais.forEach(adicional => {
                const nome = adicional.nome?.trim() || 'Adicional';
                acc[nome] = (acc[nome] || 0) + (adicional.valor || 0);
            });
        }
        return acc;
    }, {});

    const mainTableFootRow = camposParaExportar.map((campo, index) => {
      if (index === 0) return { content: 'TOTAL', isHeader: true };
      
      const isNumeric = campo.type === 'currency' || campo.type === 'number';
      if(isNumeric) {
          const total = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, campo.id, 0) || 0), 0);
          const content = campo.type === 'currency' ? formatarMoeda(total) : formatNumber(total);
          return { content };
      }
      return { content: '' };
    });

    let resumoAgrupado = null;
    if (agrupamento) {
        const nomeAgrupamento = { 'expositor_cidade': 'Cidade', 'expositor_estado': 'Estado', 'expositor_pais': 'País', 'comercial.nome': 'Vendedor' }[agrupamento];
        const dados = dadosOrdenados.reduce((acc, item) => {
            const chave = getNestedValue(item, agrupamento, 'Não especificado');
            if (!acc[chave]) acc[chave] = { count: 0, area: 0, valor: 0 };
            acc[chave].count++;
            acc[chave].area += getNestedValue(item, 'area', 0) || 0;
            acc[chave].valor += getNestedValue(item, 'valor_final_calculado', 0) || 0;
            return acc;
        }, {});
        resumoAgrupado = { nomeAgrupamento, dados };
    }

    return {
        geral: { totalEspacosVendidos, totalEspacosLivres, totalEspacosPendentes, totalEspacosRenovados, totalEspacosReservados, metragemVendida, metragemBonificada, metragemLivre, metragemReservada, metragemPendenteRenovacao, metragemRenovada, metragemCobrada, valorTotalVendido, valorEspaco, valorKVA, valorEstacionamento, totaisAdicionais, quantidadeEstacionamento, quantidadeKVA, contagemStatus, // adicionar a contagem por status
        },
        mainTableFooter: mainTableFootRow,
        agrupado: resumoAgrupado,
        camposParaExportar: camposParaExportar,
        
    };
  };

const calcularTodosResumosPDF = (dadosOrdenados, camposSelecionados) => {
    let camposParaExportar = camposDisponiveis.filter(c => camposSelecionados.includes(c.id));

    const criarColunaAgrupada = (camposOriginais, idsParaAgrupar, novaColunaId, novaColunaLabel) => {
        const camposSelecionadosParaAgrupar = camposOriginais.filter(c => idsParaAgrupar.includes(c.id));
        if (camposSelecionadosParaAgrupar.length > 0) {
            const colunaAgrupada = {
                id: novaColunaId,
                label: novaColunaLabel,
                accessor: (item) => camposSelecionadosParaAgrupar
                    .map(campo => `${campo.label}: ${campo.accessor(item)}`)
                    .filter(v => !v.endsWith(': --') && !v.endsWith(': '))
                    .join('\n')
            };
            const camposRestantes = camposOriginais.filter(c => !idsParaAgrupar.includes(c.id));
            return [colunaAgrupada, ...camposRestantes];
        }
        return camposOriginais;
    };

    const idsExpositor = ['expositor_nome', 'expositor_endereco', 'expositor_email', 'expositor_telefone', 'expositor_whatsapp'];
    const camposAposAgruparExpositor = criarColunaAgrupada(camposParaExportar, idsExpositor, 'detalhesExpositor', 'Detalhes do Expositor');
    
    let camposProcessados = [...camposAposAgruparExpositor];
    const idxDetalhesExpositor = camposProcessados.findIndex(c => c.id === 'detalhesExpositor');
    if (idxDetalhesExpositor > -1) {
        const [detalhesExpositor] = camposProcessados.splice(idxDetalhesExpositor, 1);
        const idxFeira = camposProcessados.findIndex(c => c.id === 'feira.nome');
        camposProcessados.splice(idxFeira !== -1 ? idxFeira + 1 : 1, 0, detalhesExpositor);
    }
    
    // REMOVER 'valor_total_adicionais' do agrupamento para evitar duplicação
    const idsEstande = ['valor_total_estande_metro', 'area', 'metragem_bonificada', 'valor_kva_tabela', 'valor_estacionamento', 'valor_final_calculado', 'quantidade_estacionamento', 'quantidade_kva_tabela'];
    camposProcessados = criarColunaAgrupada(camposProcessados, idsEstande, 'detalhesEstande', 'Detalhes do Espaço');

    const idxDetalhesEstande = camposProcessados.findIndex(c => c.id === 'detalhesEstande');
    if (idxDetalhesEstande > -1) {
        const [detalhesEstande] = camposProcessados.splice(idxDetalhesEstande, 1);
        const idxNomeEstande = camposProcessados.findIndex(c => c.id === 'nome_estande');
        camposProcessados.splice(idxNomeEstande !== -1 ? idxNomeEstande + 1 : camposProcessados.length, 0, detalhesEstande);
    }
    
    camposParaExportar = camposProcessados;
    
    const contagemStatus = contarEspacosPorStatus(dadosOrdenados); // Adicionado: Contagem de status
    const totalEspacosVendidos = contagemStatus.vendidos; // Vendidos
    const totalEspacosRenovados = contagemStatus.renovados; // Vendidos + Renovados
    const totalEspacosPendentes = contagemStatus.pendentes; // Vendidos + Renovados
    const totalEspacosReservados = contagemStatus.reservados; // Vendidos + Renovados
    const totalEspacosLivres = contagemStatus.livres; // Vendidos + Renovados
    
    const metragemRenovada = dadosOrdenados.filter(item => item.status_reserva_id === 5).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Livre
    const metragemPendenteRenovacao= dadosOrdenados.filter(item => item.status_reserva_id === 6).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Livre
    
    const metragemVendida = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0);
    const metragemBonificada = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'metragem_bonificada', 0) || 0), 0);
    const metragemLivre = dadosOrdenados.filter(item => item.status_reserva_id === 1).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Livre
    const metragemReservada = dadosOrdenados.filter(item => item.status_reserva_id === 2).reduce((sum, item) => sum + (getNestedValue(item, 'area', 0) || 0), 0); // Adicionado: Metragem Reservada
    
    const valorTotalVendido = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_final_calculado', 0) || 0), 0);
    const metragemCobrada = metragemVendida - metragemBonificada;
    const valorEspaco = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_total_estande_metro', 0) || 0), 0);
    const valorKVA = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_kva_tabela', 0) || 0), 0);
    const valorEstacionamento = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'valor_estacionamento', 0) || 0), 0);
    const quantidadeKVA = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'quantidade_kva_tabela', 0) || 0), 0);
    const quantidadeEstacionamento = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, 'quantidade_estacionamento', 0) || 0), 0);
    
    const totaisAdicionais = dadosOrdenados.reduce((acc, item) => {
        const adicionais = getNestedValue(item, 'adicionais_detalhados', []);
        if (Array.isArray(adicionais)) {
            adicionais.forEach(adicional => {
                const nome = adicional.nome?.trim() || 'Adicional';
                acc[nome] = (acc[nome] || 0) + (adicional.valor || 0);
            });
        }
        return acc;
    }, {});

    const mainTableFootRow = camposParaExportar.map((campo, index) => {
      if (index === 0) return { content: 'TOTAL', isHeader: true };
      
      const isNumeric = campo.type === 'currency' || campo.type === 'number';
      if(isNumeric) {
          const total = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, campo.id, 0) || 0), 0);
          const content = campo.type === 'currency' ? formatarMoeda(total) : formatNumber(total);
          return { content };
      }
      return { content: '' };
    });

    let resumoAgrupado = null;
    if (agrupamento) {
        const nomeAgrupamento = { 'expositor_cidade': 'Cidade', 'expositor_estado': 'Estado', 'expositor_pais': 'País', 'comercial.nome': 'Vendedor' }[agrupamento];
        const dados = dadosOrdenados.reduce((acc, item) => {
            const chave = getNestedValue(item, agrupamento, 'Não especificado');
            if (!acc[chave]) acc[chave] = { count: 0, area: 0, valor: 0 };
            acc[chave].count++;
            acc[chave].area += getNestedValue(item, 'area', 0) || 0;
            acc[chave].valor += getNestedValue(item, 'valor_final_calculado', 0) || 0;
            return acc;
        }, {});
        resumoAgrupado = { nomeAgrupamento, dados };
    }

    return {
        geral: { totalEspacosVendidos, totalEspacosLivres, totalEspacosPendentes, totalEspacosRenovados, totalEspacosReservados, metragemVendida, metragemBonificada, metragemLivre, metragemReservada, metragemCobrada, metragemRenovada, metragemPendenteRenovacao, valorTotalVendido, valorEspaco, valorKVA, valorEstacionamento, totaisAdicionais, quantidadeEstacionamento, quantidadeKVA, contagemStatus },
        mainTableFooter: mainTableFootRow,
        agrupado: resumoAgrupado,
        camposParaExportar: camposParaExportar,
    };
};

const gerarPDF = async () => {
  const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
  if (!filteredData || filteredData.length === 0) { toast.error('Nenhum dado para exportar'); return; }
  const dadosOrdenados = ordenarDados(filteredData);
  
  const resumos = calcularTodosResumosPDF(dadosOrdenados, camposSelecionados);
  const camposParaExportar = resumos.camposParaExportar;
  
  // Definição da função auxiliar drawLine movida para o topo da função.
  let yPosition;
  const drawLine = (label, value) => {
    doc.setFont('helvetica', 'bold'); doc.text(label, margin, yPosition);
    doc.setFont('helvetica', 'normal'); doc.text(String(value), margin + 60, yPosition, { align: 'left' }); 
    yPosition += 6;
  };

  // Modificar o accessor da coluna "Detalhes do Espaço" para incluir adicionais em linhas separadas
  const camposComAdicionaisDetalhados = camposParaExportar.map(campo => {
    if (campo.id === 'detalhesEstande') {
      return {
        ...campo,
        accessor: (item) => {
          const textoOriginal = campo.accessor(item);
          
          const adicionais = getNestedValue(item, 'adicionais_detalhados', []);
          
          if (!Array.isArray(adicionais) || adicionais.length === 0) {
            return textoOriginal;
          }
          
          let textoComAdicionais = textoOriginal;
          adicionais.forEach(adicional => {
            const nome = adicional.nome?.trim() || 'Adicional';
            const valor = adicional.valor ? formatarMoeda(adicional.valor) : 'R$ --';
            if (adicional.valor && adicional.valor > 0) {
              textoComAdicionais += `\n${nome}: ${valor}`;
            }
          });
          
          return textoComAdicionais;
        }
      };
    }
    return campo;
  });

  const tableData = dadosOrdenados.map(item => 
    camposComAdicionaisDetalhados.map(campo => campo.accessor(item) ?? '')
  );

  const doc = new jsPDF({ orientation: "landscape", unit: 'mm', format: 'a4', compress: true });
  const margin = 10, pageWidth = doc.internal.pageSize.getWidth(), primaryColor = [54, 54, 54];
  
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...primaryColor); doc.setFontSize(16);
  doc.text(nomeRelatorio, pageWidth / 2, 15, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text(`Gerado em: ${moment().format('DD/MM/YYYY HH:mm')}`, pageWidth - margin, 12, { align: 'right' });
  doc.setDrawColor(...primaryColor); doc.setLineWidth(0.5); doc.line(margin, 25, pageWidth - margin, 25);
  
  const mainTableOptions = {
    startY: 30, 
    head: [camposComAdicionaisDetalhados.map(c => c.label)], 
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9 },
    footStyles: { fillColor: [230, 230, 230], textColor: primaryColor, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    // Aumentar o lineHeight para acomodar múltiplas linhas
    styles: { lineHeight: 1.5, cellPadding: 2 }
  };

  const hasTotals = resumos.mainTableFooter.some(cell => !cell.isHeader && cell.content && cell.content.trim() !== '');

  if (hasTotals) {
    mainTableOptions.foot = [resumos.mainTableFooter.map(cell => {
      const styles = { halign: cell.isHeader ? 'left' : 'right' };
      return { content: cell.content, styles };
    })];
    mainTableOptions.showFoot = 'lastPage';
  }

  autoTable(doc, mainTableOptions);
  
  yPosition = doc.lastAutoTable.finalY + 10;
  const checarNovaPagina = (pos, espaco) => {
    if (pos > doc.internal.pageSize.getHeight() - espaco) { doc.addPage(); return 20; }
    return pos;
  };
  
  yPosition = checarNovaPagina(yPosition, 80);
  const rGeral = resumos.geral;
  
  drawLine('Espaços Vendidos', rGeral.contagemStatus.vendidos);
  drawLine('Espaços Livres', rGeral.contagemStatus.livres);
  drawLine('Espaços Reservados', rGeral.contagemStatus.reservados);
  drawLine('Espaços Cancelados', rGeral.contagemStatus.cancelados);

   if (feiraRenovada) {
    drawLine("Espaços Renovados", rGeral.contagemStatus.renovados || 0);
    drawLine("Pendentes Renovação", rGeral.contagemStatus.pendentes || 0);
  }

  drawLine('TOTAL GERAL', dadosOrdenados.length);
  
  if (resumos.agrupado) {
    yPosition = checarNovaPagina(yPosition, 60);
    const rAgrupado = resumos.agrupado;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(`TOTAIS POR ${rAgrupado.nomeAgrupamento.toUpperCase()}`, margin, yPosition); yPosition += 8;
    
    const tableHead = [[
      { content: rAgrupado.nomeAgrupamento.toUpperCase(), styles: { halign: 'left' } },
      { content: 'CLIENTES', styles: { halign: 'center' } },
      { content: '%', styles: { halign: 'right' } },
      { content: 'METRAGEM', styles: { halign: 'right' } },
      { content: '%', styles: { halign: 'right' } },
      { content: 'VALOR', styles: { halign: 'right' } }
    ]];

    const chaves = Object.keys(rAgrupado.dados).sort((a, b) => rAgrupado.dados[b].count - rAgrupado.dados[a].count);
    const body = chaves.map(chave => [ 
      chave, 
      rAgrupado.dados[chave].count, 
      `${formatNumber((rAgrupado.dados[chave].count / rGeral.totalEspacosVendidos) * 100)}%`, 
      formatNumber(rAgrupado.dados[chave].area), 
      `${formatNumber((rAgrupado.dados[chave].area / rGeral.metragemVendida) * 100)}%`, 
      formatarMoeda(rAgrupado.dados[chave].valor) 
    ]);
    
    const foot = [[ 
      { content: 'TOTAIS GERAIS', styles: { fontStyle: 'bold' } }, 
      { content: rGeral.totalEspacosVendidos, styles: { halign: 'center' } }, 
      { content: '100,00%', styles: { halign: 'right' } }, 
      { content: formatNumber(rGeral.metragemVendida), styles: { halign: 'right' } }, 
      { content: '100,00%', styles: { halign: 'right' } }, 
      { content: formatarMoeda(rGeral.valorTotalVendido), styles: { halign: 'right' } } 
    ]];

    autoTable(doc, {
      startY: yPosition, head: tableHead, body, foot,
      margin: { left: margin, right: margin }, theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9, fontStyle: 'bold' },
      footStyles: { fillColor: [230, 230, 230], textColor: primaryColor, fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 
        0: { fontStyle: 'bold' }, 
        1: { halign: 'center' }, 
        2: { halign: 'right' }, 
        3: { halign: 'right' }, 
        4: { halign: 'right' }, 
        5: { halign: 'right' } 
      },
      styles: { lineHeight: 1.3 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 10;
    yPosition = checarNovaPagina(yPosition, 80);
  }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('TOTAIS GERAIS', margin, yPosition); yPosition += 8;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  
  // A função drawLine foi movida para o topo, removendo a necessidade desta redefinição.
  
  drawLine('Espaços Vendidos', rGeral.totalEspacosVendidos); 
  drawLine('Espaços Livres', rGeral.totalEspacosLivres); 
  drawLine('Espaços Reservados', rGeral.totalEspacosReservados); 
  
  if (feiraRenovada) {
    drawLine('Espaços Renovados', rGeral.totalEspacosRenovados); 
    drawLine('Espaços Pend.Renovação', rGeral.totalEspacosPendentes); 
  }

  drawLine('Metragem Vendida', formatNumber(rGeral.metragemVendida));
  drawLine('Metragem Livre', formatNumber(rGeral.metragemLivre)); // Metragem Livre
  
  if (feiraRenovada) {
    drawLine('Metragem Renovada', formatNumber(rGeral.metragemRenovada)); // Metragem Livre
    drawLine('Metragem Pend.Renovação', formatNumber(rGeral.metragemPendenteRenovacao)); // Metragem Livre
  }

  drawLine('Metragem Reservada', formatNumber(rGeral.metragemReservada)); // Metragem Livre
  drawLine('Metragem Bonificada', formatNumber(rGeral.metragemBonificada)); 
  drawLine('Metragem Cobrada', formatNumber(rGeral.metragemCobrada));
  drawLine('Valor Total Vendido', formatarMoeda(rGeral.valorTotalVendido)); 
  drawLine('Valor Espaço', formatarMoeda(rGeral.valorEspaco));
  drawLine('Valor KVA', formatarMoeda(rGeral.valorKVA));
  drawLine('Quantidade KVA', rGeral.quantidadeKVA);
  drawLine('Valor Estacionamento', formatarMoeda(rGeral.valorEstacionamento));
  drawLine('Quantidade Estacionamento', rGeral.quantidadeEstacionamento);

  // Adicionar cada adicional específico em linhas separadas
  if (rGeral.totaisAdicionais) {
    Object.entries(rGeral.totaisAdicionais).forEach(([nome, total]) => {
      // Só mostrar adicionais com valor maior que zero
      if (total > 0) {
        drawLine(`Valor ${nome}`, formatarMoeda(total));
      }
    });
  }

  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(100);
    doc.text(`Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`, margin, pageHeight - 10, { align: 'left' });
    try { doc.addImage(logo, 'PNG', (pageWidth - 20) / 2, pageHeight - 15, 20, 8); } catch (e) { /* silent */ }
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  doc.save(`${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.pdf`);
  setIsExportacaoAvancadaOpen(false);
  toast.success('PDF gerado com sucesso!');
};

  const exportarXLSX = () => {
  try {
    const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
    if (!filteredData || filteredData.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }
    
    const dadosOrdenados = ordenarDados(filteredData);
    
    const tiposAdicionais = [];
    dadosOrdenados.forEach(item => {
      const adicionais = getNestedValue(item, 'adicionais_detalhados', []);
      if (Array.isArray(adicionais)) {
        adicionais.forEach(adicional => {
          const nome = adicional.nome?.trim() || 'Adicional';
          if (!tiposAdicionais.includes(nome)) {
            tiposAdicionais.push(nome);
          }
        });
      }
    });

    const camposExportar = camposDisponiveis.filter(c => camposSelecionados.includes(c.id));
    const camposSemAdicionais = camposExportar.filter(campo => campo.id !== 'valor_total_adicionais');
    const colunasAdicionais = tiposAdicionais.map(nomeAdicional => ({
      id: `adicional_${nomeAdicional}`,
      label: `Valor ${nomeAdicional}`,
      accessor: item => {
        const adicionais = getNestedValue(item, 'adicionais_detalhados', []);
        const adicional = adicionais.find(a => a.nome?.trim() === nomeAdicional);
        return adicional ? adicional.valor : 0;
      },
      type: 'currency'
    }));

    // Combinar todas as colunas
    const camposParaExportar = [...camposSemAdicionais, ...colunasAdicionais];
    
    const resumos = calcularTodosResumos(dadosOrdenados, camposSelecionados);

    // --- 1. Preparar dados para a Planilha "Relatório" com colunas separadas para adicionais ---
    const relatorioHeaders = camposParaExportar.map(c => c.label);
    const relatorioBody = dadosOrdenados.map(item =>
      camposParaExportar.map(campo => {
        const isNumericType = campo.type === 'currency' || campo.type === 'number';
        
        // Para colunas de adicionais específicos, usar o acessor personalizado
        if (campo.id.startsWith('adicional_')) {
          const valor = campo.accessor(item);
          return isNumericType ? valor : formatarMoeda(valor);
        }
        
        // Para XLSX, usamos números brutos para permitir formatação e fórmulas no Excel.
        if (isNumericType) {
          return getNestedValue(item, campo.id, null);
        }
        // Para outros tipos (status, texto, data), usamos o acessor que formata o valor.
        return campo.accessor(item);
      })
    );
    
    // Dentro da função exportarXLSX, na seção relatorioFooter
const relatorioFooter = camposParaExportar.map((campo, index) => {
    if (index === 0) return 'TOTAL';

    const isNumericType = campo.type === 'currency' || campo.type === 'number';

    // Pula colunas agrupadas (se existirem)
    if (campo.id === 'detalhesExpositor' || campo.id === 'detalhesEstande') {
        return null;
    }

    if (isNumericType) {
        let total;
        // Acessa o valor bruto para soma, usando o accessor para campos dinâmicos (adicionais) 
        // ou getNestedValue para campos estáticos (incluindo 'area')
        if (campo.id.startsWith('adicional_')) {
            total = dadosOrdenados.reduce((sum, item) => sum + (campo.accessor(item) || 0), 0);
        } else {
            total = dadosOrdenados.reduce((sum, item) => sum + (getNestedValue(item, campo.id, 0) || 0), 0);
        }
        return total; // Retorna o valor bruto (número)
    }
    return null; // Retorna null para não numéricos
});

    const relatorioSheetData = [relatorioHeaders, ...relatorioBody, relatorioFooter];
    const wsRelatorio = XLSX.utils.aoa_to_sheet(relatorioSheetData);
    
    // --- 2. Aplicar formatação de número/moeda na planilha "Relatório" ---
    const BRL_FORMAT = '"R$" #,##0.00';
    const NUM_FORMAT = '#,##0.00';
    const lastRow = relatorioSheetData.length - 1;

    camposParaExportar.forEach((campo, colIndex) => {
      if (campo.type === 'currency' || campo.type === 'number') {
        const format = campo.type === 'currency' ? BRL_FORMAT : NUM_FORMAT;
        for (let rowIndex = 1; rowIndex <= lastRow; rowIndex++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
          const cell = wsRelatorio[cellAddress];
          if (cell && typeof cell.v === 'number') {
            cell.t = 'n';
            cell.z = format;
          }
        }
      }
    });

    // --- 3. Preparar dados para a Planilha "Resumos" com adicionais discriminados ---
    const rGeral = resumos.geral;
    let resumosSheetData = [
      ['RESUMO GERAL'],
      ['Espaços Vendidos', rGeral.contagemStatus.vendidos],
      ['Espaços Livres', rGeral.contagemStatus.livres],
      ['Espaços Reservados', rGeral.contagemStatus.reservados],
      ['Espaços Cancelados', rGeral.contagemStatus.cancelados],
      ...(feiraRenovada ? [
        ['Espaços Renovados', rGeral.contagemStatus.renovados],
        ['Espaços Pend.Renovação', rGeral.contagemStatus.pendentes]] : []),
      ['TOTAL GERAL', dadosOrdenados.length],
      ['Metragem Vendida', rGeral.metragemVendida],
      ['Metragem Livre', rGeral.metragemLivre], // Adicionado: Metragem Livre
      ['Metragem Reservada', rGeral.metragemReservada], // Adicionado: Metragem Livre
      ...(feiraRenovada ? [
        ['Metragem Renovada', rGeral.metragemRenovada],
        ['Metragem Pend.Renovação', rGeral.metragemPendenteRenovacao]] : []),
      ['Metragem Bonificada', rGeral.metragemBonificada],
      ['Metragem Cobrada', rGeral.metragemCobrada],
      ['Valor Total Vendido', rGeral.valorTotalVendido],
      ['Valor Espaço', rGeral.valorEspaco],
      ['Valor KVA', rGeral.valorKVA],
      ['Quantidade KVA', rGeral.quantidadeKVA],
      ['Valor Estacionamento', rGeral.valorEstacionamento],
      ['Quantidade Estacionamento', rGeral.quantidadeEstacionamento],
    ];

    // Adicionar cada adicional específico ao resumo
    if (rGeral.totaisAdicionais) {
      Object.entries(rGeral.totaisAdicionais).forEach(([nome, total]) => {
        resumosSheetData.push([`Valor ${nome}`, total]);
      });
    }

    if (resumos.agrupado) {
      const rAgrupado = resumos.agrupado;
      resumosSheetData.push([]);
      resumosSheetData.push([`RESUMO AGRUPADO POR ${rAgrupado.nomeAgrupamento.toUpperCase()}`]);
      resumosSheetData.push([rAgrupado.nomeAgrupamento, 'Clientes', 'Metragem', 'Valor']);

      Object.keys(rAgrupado.dados).sort((a,b) => a.localeCompare(b)).forEach(chave => {
        const grupo = rAgrupado.dados[chave];
        resumosSheetData.push([chave, grupo.count, grupo.area, grupo.valor]);
      });
      resumosSheetData.push(['TOTAL GERAL', rGeral.totalEspacosVendidos, rGeral.metragemVendida, rGeral.valorTotalVendido]);
    }
    const wsResumos = XLSX.utils.aoa_to_sheet(resumosSheetData);

    // --- 4. Aplicar formatação na planilha "Resumos" ---
    const rangeResumos = XLSX.utils.decode_range(wsResumos['!ref']);
    let inGroupedSummary = false;
    for (let R = rangeResumos.s.r; R <= rangeResumos.e.r; ++R) {
      const cellA = wsResumos[XLSX.utils.encode_cell({ r: R, c: 0 })];
      if (!cellA || !cellA.v) continue;

      if (cellA.v.toString().startsWith('RESUMO AGRUPADO')) {
        inGroupedSummary = true;
        continue;
      }

      if (inGroupedSummary) {
        const cellMetragem = wsResumos[XLSX.utils.encode_cell({ r: R, c: 2 })];
        if (cellMetragem && typeof cellMetragem.v === 'number') {
          cellMetragem.t = 'n';
          cellMetragem.z = NUM_FORMAT;
        }
        const cellValor = wsResumos[XLSX.utils.encode_cell({ r: R, c: 3 })];
        if (cellValor && typeof cellValor.v === 'number') {
          cellValor.t = 'n';
          cellValor.z = BRL_FORMAT;
        }
      } else {
        const label = cellA.v.toString().toLowerCase();
        const cellB = wsResumos[XLSX.utils.encode_cell({ r: R, c: 1 })];
        if (cellB && typeof cellB.v === 'number') {
          if (label.includes('valor')) {
            cellB.t = 'n';
            cellB.z = BRL_FORMAT;
          } else if (label.includes('metragem')) {
            cellB.t = 'n';
            cellB.z = NUM_FORMAT;
          }
        }
      }
    }

    // --- 5. Auto-ajuste da largura das colunas ---
    const fitToColumn = (data) => {
      if (!data || data.length === 0) return [];
      const C_WIDTH = 1.1;
      return data[0].map((a, i) => ({
        wch: Math.max(...data.map(a2 => (a2[i] ? a2[i].toString().length : 0))) * C_WIDTH,
      }));
    };
    wsRelatorio['!cols'] = fitToColumn(relatorioSheetData);
    wsResumos['!cols'] = fitToColumn(resumosSheetData);

    // --- 6. Criar o Workbook e fazer o download ---
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsRelatorio, "Relatório");
    XLSX.utils.book_append_sheet(wb, wsResumos, "Resumos");

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

    saveAs(dataBlob, `${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
    toast.success('XLSX exportado com sucesso!');
    setIsExportacaoAvancadaOpen(false);

  } catch (error) {
    console.error('Erro ao exportar XLSX:', error);
    toast.error('Falha ao exportar XLSX');
  }
  };

  const paisesFilterOptions = useMemo(() => paisesOptions.map(p => ({ value: p.sigla, label: p.nome || p.nome })), [paisesOptions]);
  const estadosFilterOptions = useMemo(() => estadosOptions.map(e => ({ value: e.sigla, label: e.nome })), [estadosOptions]);
  const cidadesFilterOptions = useMemo(() => cidadesOptions.map(c => ({ value: c.nome, label: c.nome })), [cidadesOptions]);
  const pavilhoesFilterOptions = useMemo(() => pavilhoesOptions.map(p => ({ value: p.nome, label: p.nome })), [pavilhoesOptions]);

  const getVisiblePages = (pageCount, currentIndex, max = 7) => {
    const last = pageCount - 1;
    if (pageCount <= max) return Array.from({ length: pageCount }, (_, i) => i);

    const windowSize = Math.max(1, Math.floor((max - 3) / 2));
    let start = Math.max(1, currentIndex - windowSize);
    let end = Math.min(last - 1, currentIndex + windowSize);

    const need = (max - 2) - (end - start + 1);
    if (need > 0) {
      if (start === 1) {
        end = Math.min(last - 1, end + need);
      } else if (end === last - 1) {
        start = Math.max(1, start - need);
      }
    }

    const middle = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    return [0, ...(start > 1 ? ['...'] : []), ...middle, ...(end < last - 1 ? ['...'] : []), last];
  };

  return (
    <Fragment>
      <ToastContainer closeButton={false} autoClose={3000} />
      {isGlobalFilter && (
        <Row className="mb-1">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <Row className="g-3">
              {feirasOptions.length > 0 && (
                <>
                  <Col md={3}>
                    <Select id="feira-select" name="feira_id" placeholder="Selecione a feira..." value={feirasOptions.find(f => f.value === feiraSelecionada)} onChange={option => onFeiraChange(option ? option.value : '')} options={feirasOptions} isClearable />
                  </Col>
                   </> 
              )}
              <Col md={3}>
                  <Select
                      id="pais-select" name="pais_id"
                      placeholder="Todos os países"
                      value={paisesFilterOptions.find(p => p.value === paisSelecionado)}
                      onChange={option => onPaisChange(option ? option.value : '')}
                      options={paisesFilterOptions}
                      isDisabled={!feiraSelecionada && !dashboard}
                      isClearable
                  />
              </Col>
               
              {paisSelecionado === 'BR' && (
                <>
                  <Col md={3}>
                      <Select
                          id="estados-select" name="estados_id"
                          placeholder="Todos os estados"
                          value={estadosFilterOptions.find(e => e.value === estadoSelecionado)}
                          onChange={option => onEstadoChange(option ? option.value : '')}
                          options={estadosFilterOptions}
                          isDisabled={estadosOptions.length === 0}
                          isClearable
                      />
                  </Col>
                  <Col md={3}>
                      <Select
                          id="cidades-select" name="cidades_id"
                          placeholder="Todas as cidades"
                          value={cidadesFilterOptions.find(c => c.value === cidadeSelecionada)}
                          onChange={option => onCidadeChange(option ? option.value : '')}
                          options={cidadesFilterOptions}
                          isDisabled={cidadesOptions.length === 0}
                          isClearable
                      />
                  </Col>
                </>
              )}
              <Col md={3}>
                  <Select
                      id="pavilhao-select" name="pavilhao_id"
                      placeholder="Todos os pavilhões"
                      value={pavilhoesFilterOptions.find(p => p.value === pavilhaoSelecionado)}
                      onChange={option => onPavilhaoChange(option ? option.value : '')}
                      options={pavilhoesFilterOptions}
                      isDisabled={!feiraSelecionada && !dashboard}
                      isClearable
                  />
              </Col>
              <Col md={3}>
                <Select
                  id="status-select"
                  placeholder="Todos os Status"
                  // Use 'optionsStatus' que definimos acima, não 'statusOptions'
                  options={optionsStatus} 
                  value={optionsStatus.find(s => s.value === statusSelecionado)}
                  onChange={option => onStatusChange(option ? option.value : '')}
                  isClearable
                />
              </Col>
              <Col md={4}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <Input value={globalFilter ?? ''} onChange={e => setGlobalFilter(e.target.value)} placeholder={SearchPlaceholder || "Pesquisar..."} className="form-control" id="pesquisa-input" name="pesquisa_id"/>
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
              <Col md={3} className="align-items-end">
                <Button color="primary" onClick={() => setIsExportacaoAvancadaOpen(true)} disabled={!data || data.length === 0} id="exportar_botao" name="exportar_botao">
                  <i className="ri-file-export-line align-bottom me-1"></i> Exportar
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Row>
      )}

      <div className={divClassName}>
        <Table hover className={tableClassName}>
          <thead className={theadClassName}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} {...{ onClick: header.column.getToggleSortingHandler() }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <Col sm>
          <div className="text-muted">
            Mostrando <span className="fw-semibold ms-1">{getRowModel().rows.length}</span> de{" "}
            <span className="fw-semibold">{data.length}</span> Resultados
          </div>
        </Col>

        <Col sm="auto">
          <ul
            className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0"
            style={{ flexWrap: 'wrap', rowGap: '8px' }}
          >
            <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={previousPage} aria-label="Página anterior">
                Anterior
              </Link>
            </li>

            {(() => {
              const pageCount = table.getPageCount();
              const current = getState().pagination.pageIndex;
              const pages = getVisiblePages(pageCount, current, 9);

              return pages.map((p, idx) => {
                if (p === '...') {
                  return (
                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  );
                }
                const isActive = current === p;
                return (
                  <li key={p} className={`page-item${isActive ? ' active' : ''}`}>
                    <Link
                      to="#"
                      className={`page-link${isActive ? ' active' : ''}`}
                      onClick={() => setPageIndex(p)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {p + 1}
                    </Link>
                  </li>
                );
              });
            })()}

            <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={nextPage} aria-label="Próxima página">
                Próximo
              </Link>
            </li>
          </ul>
        </Col>
      </Row>

      
      <Modal isOpen={isExportacaoAvancadaOpen} toggle={() => setIsExportacaoAvancadaOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsExportacaoAvancadaOpen(false)}>Exportação Avançada</ModalHeader>
        <ModalBody>
          <p className="text-muted">Selecione os campos e configure as opções para o seu relatório.</p>
          <Row>
            <Col md={6}>
              <Card>
                <CardHeader><h6 className="mb-0">Colunas da Tabela</h6></CardHeader>
                <CardBody style={{ minHeight: '220px', maxHeight: '220px', overflowY: 'auto' }}>
                  <FormGroup>
                    {camposDasColunas.map(campo => (
                      <div key={campo.id} className="form-check">
                        <Input type="checkbox" id={`campo-${campo.id}`} checked={camposSelecionados.includes(campo.id)} onChange={() => toggleCampo(campo.id)} />
                        <Label htmlFor={`campo-${campo.id}`} check>{campo.label}</Label>
                      </div>
                    ))}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card>
                <CardHeader><h6 className="mb-0">Dados Adicionais</h6></CardHeader>
                <CardBody style={{ minHeight: '220px', maxHeight: '220px', overflowY: 'auto' }}>
                  <FormGroup>
                    {camposAdicionais.map(campo => (
                      <div key={campo.id} className="form-check">
                        <Input type="checkbox" id={`campo-${campo.id}`} checked={camposSelecionados.includes(campo.id)} onChange={() => toggleCampo(campo.id)} />
                        <Label htmlFor={`campo-${campo.id}`} check>{campo.label}</Label>
                      </div>
                    ))}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col md={6} className="mt-3">
              <Card>
                <CardHeader><h6 className="mb-0">Opções de Ordenação</h6></CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label htmlFor="ordenacao">Ordenar por:</Label>
                    <Input bsSize="sm" type="select" id="ordenacao" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
                      {camposDisponiveis.map(campo => (<option key={campo.id} value={campo.id}>{campo.label}</option>))}
                    </Input>
                  </FormGroup>
                  <FormGroup check>
                    <Input type="checkbox" id="ordemDescendente" checked={ordemDescendente} onChange={() => setOrdemDescendente(!ordemDescendente)} />
                    <Label htmlFor="ordemDescendente" check>Ordem Decrescente</Label>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col md={6} className="mt-3">
              <Card>
                <CardHeader><h6 className="mb-0">Opções de Resumo</h6></CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label htmlFor="agrupamento">Agrupar totais por:</Label>
                    <Input bsSize="sm" type="select" id="agrupamento" value={agrupamento} onChange={(e) => setAgrupamento(e.target.value)}>
                      <option value="">Nenhum</option>
                      <option value="expositor_cidade">Cidade</option>
                      <option value="expositor_estado">Estado</option>
                      <option value="expositor_pais">País</option>
                      <option value="comercial.nome">Vendedor</option>
                    </Input>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsExportacaoAvancadaOpen(false)}>Cancelar</Button>
          <div className="d-flex gap-2">
            <Button color="primary" onClick={gerarPDF}><i className="ri-file-pdf-line align-bottom me-1"></i> PDF</Button>
            <Button color="success" onClick={exportarXLSX}>
              <i className="ri-file-excel-2-line align-bottom me-1"></i> Excel
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default TabelaFiltroPaginacao;